import type { MinimalRequiredUppyFile } from '@uppy/core';
import type { RespondedType } from '@/models/response';
import type { Uploadable, UppyFileMeta } from '@/models/uploadable';
import { getUserId } from '@/stores/auth';

export function convertBlobToUploadable(blob: Blob, filename = 'unknown.blob'): Uploadable {
  return {
    url: URL.createObjectURL(blob),
    name: filename,
    size: blob.size,
    type: blob.type,
    compress: true,
  };
}

export function convertFileToUploadable(file: File): Uploadable {
  return {
    url: URL.createObjectURL(file),
    name: file.name,
    size: file.size,
    type: file.type,
    compress: false,
  };
}

export function convertUrlToUploadable(input: string | URL, type = ''): Uploadable {
  const url = input.toString();
  let mimeType = type;

  const isDataUrl = url.startsWith('data:') && url.includes('base64,');
  if (isDataUrl) {
    const [header = ''] = url.split(',');
    mimeType = header.split(';').at(0)?.replace('data:', '').trim() || type;
  }

  return {
    url: url,
    name: '',
    size: 0,
    type: mimeType,
    compress: false,
  };
}

export async function convertUploadableToBlob(uploadable: Uploadable): Promise<Blob> {
  return fetch(uploadable.url).then((res) => res.blob());
}

export async function convertUploadableToFile(uploadable: Uploadable): Promise<File> {
  const fileBlob = await convertUploadableToBlob(uploadable);

  return new File([fileBlob], uploadable.name, { type: uploadable.type });
}

export async function convertUploadableToUppyFile(
  uploadable: Uploadable,
  meta: Partial<UppyFileMeta> = {},
): Promise<MinimalRequiredUppyFile<UppyFileMeta, RespondedType>> {
  const fileBlob = await convertUploadableToBlob(uploadable);
  const userId = getUserId();

  return {
    name: uploadable.name,
    type: uploadable.type,
    data: fileBlob,
    meta: {
      FileName: uploadable.name,
      FileType: uploadable.type,
      UID: userId ?? 0,
      ...meta,
    },
  };
}

export async function convertUploadableToDataURL(uploadable: Uploadable): Promise<string> {
  const fileBlob = await convertUploadableToBlob(uploadable);

  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result as string);
    reader.onerror = () => reject(reader.error);
    reader.readAsDataURL(fileBlob);
  });
}
